import { router } from "@inertiajs/react"
import { MoreHorizontal, RefreshCw, Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

/**
 * OKS-21 — Overflow menu for invite-panel secondary actions.
 *
 * Houses **Regenerate** + **Revoke** behind a `…` menu, separate
 * from the primary Copy/Share row. Each menu item opens a
 * confirmation `AlertDialog` (UX Spec §1 micro-interactions —
 * destructive variant on Revoke).
 *
 * **Backend contract (BE TRD §API Surface):**
 *   - Regenerate = POST `candidateInvitePath(candidate_id)` (same URL
 *     as initial generate; idempotent in the BE service — if an
 *     active invite exists, the GenerateService revokes it + creates
 *     a new row in one transaction).
 *   - Revoke = DELETE `candidateInvitePath(candidate_id)`.
 *
 * No body params — both endpoints take none. `preserveScroll: true`
 * keeps the panel scroll position; flash-driven sonner toasts via
 * `useFlash` already mounted in `PersistentLayout`.
 *
 * **Why `router.post` / `router.delete` instead of Inertia `<Form>`:**
 * the actions are buttons inside a confirm dialog flow — wrapping
 * a `<Form>` around an AlertDialog footer is awkward and the
 * render-prop processing flag fights the dialog's internal state.
 * `router.*` from `@inertiajs/react` is the equivalent imperative
 * API and runs through the same Inertia request pipeline. Per
 * Crew rules: no react-hook-form, no zod, no shadcn `Form` — those
 * apply specifically to FORM-shaped surfaces (input fields). Pure
 * action buttons are fine via `router`.
 */

interface InviteOverflowMenuProps {
  /** POST URL — non-null when an active invite exists OR candidate is
   *  approved (`invite_actions.generate_url`). */
  regenerateUrl: string | null
  /** DELETE URL — non-null when an active invite exists
   *  (`invite_actions.revoke_url`). */
  revokeUrl: string | null
  className?: string
}

export function InviteOverflowMenu({
  regenerateUrl,
  revokeUrl,
  className,
}: InviteOverflowMenuProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [submitting, setSubmitting] = useState(false)
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  // Defensive — overflow renders only when there's at least one
  // action. Parent gates rendering, but we no-op safely if both are
  // null (e.g. mid-PATCH while flash is in flight).
  const hasAction = regenerateUrl !== null || revokeUrl !== null
  if (!hasAction) return null

  const handleRegenerate = () => {
    if (!regenerateUrl || submitting || !online) return
    setSubmitting(true)
    router.post(
      regenerateUrl,
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          setRegenerateOpen(false)
        },
      },
    )
  }

  const handleRevoke = () => {
    if (!revokeUrl || submitting || !online) return
    setSubmitting(true)
    router.delete(revokeUrl, {
      preserveScroll: true,
      onFinish: () => {
        setSubmitting(false)
        setRevokeOpen(false)
      },
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="invites-panel-overflow-trigger"
              aria-label={t("invites.panel.actions.more")}
              className={cn(className)}
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent align="end" finalFocus={finalFocus}>
          {regenerateUrl && (
            <DropdownMenuItem
              onClick={() => {
                yieldFocusToDialog()
                setRegenerateOpen(true)
              }}
              data-testid="invites-panel-regenerate-action"
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("invites.panel.actions.regenerate")}
            </DropdownMenuItem>
          )}
          {revokeUrl && (
            <DropdownMenuItem
              onClick={() => {
                yieldFocusToDialog()
                setRevokeOpen(true)
              }}
              data-testid="invites-panel-revoke-action"
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="size-4" aria-hidden="true" />
              {t("invites.panel.actions.revoke")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {regenerateUrl && (
        <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
          <AlertDialogContent data-testid="invites-panel-regenerate-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("invites.panel.regenerate_dialog.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("invites.panel.regenerate_dialog.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>
                {t("invites.panel.regenerate_dialog.cancel")}
              </AlertDialogCancel>
              {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                  `Dialog.Close` and would close before the request settles. */}
              <Button
                type="button"
                onClick={handleRegenerate}
                disabled={submitting || !online}
                data-testid="invites-panel-confirm-regenerate"
              >
                {t("invites.panel.regenerate_dialog.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {revokeUrl && (
        <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
          <AlertDialogContent data-testid="invites-panel-revoke-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("invites.panel.revoke_dialog.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("invites.panel.revoke_dialog.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>
                {t("invites.panel.revoke_dialog.cancel")}
              </AlertDialogCancel>
              {/* OKS-1023 — plain `Button`: `AlertDialogAction` is Base UI
                  `Dialog.Close` and would close before the request settles. */}
              <Button
                type="button"
                onClick={handleRevoke}
                disabled={submitting || !online}
                data-testid="invites-panel-confirm-revoke"
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {t("invites.panel.revoke_dialog.confirm")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}
